
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Trophy, Clock, MapPin, DollarSign, Copy, Info } from 'lucide-react';
import PermissionGate from '@/components/rbac/PermissionGate';
import { TeamSearchCombobox } from './TeamSearchCombobox';
import { venueGamesService } from '@/services/venueGamesService';
import { sportsService, Sport } from '@/services/sportsService';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface VenueGameSubmissionFormProps {
  venueId: string;
  onSuccess?: () => void;
}

interface VenueSection {
  id: string;
  section_name: string;
  section_type: string;
  field_id?: string;
}

interface GameFormData {
  game_title: string;
  game_description: string;
  game_date: string;
  game_time: string;
  field_location: string;
  sport_id: number;
  home_team_id?: number | string;
  away_team_id?: number | string;
  scorekeeper_needed: boolean;
  scorekeeper_points: number;
  player_participants: string[];
}

export const VenueGameSubmissionForm = ({ venueId, onSuccess }: VenueGameSubmissionFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [gameCode, setGameCode] = useState<string | null>(null);
  const [homeTeamName, setHomeTeamName] = useState('');
  const [awayTeamName, setAwayTeamName] = useState('');
  const [availableSports, setAvailableSports] = useState<Sport[]>([]);
  const [venueSections, setVenueSections] = useState<VenueSection[]>([]);
  const [loadingSections, setLoadingSections] = useState(true);
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<GameFormData>({
    defaultValues: {
      scorekeeper_needed: false,
      scorekeeper_points: 50,
      player_participants: []
    }
  });

  // Load available sports and venue sections on component mount
  useEffect(() => {
    const loadData = async () => {
      const sports = await sportsService.getAvailableSports();
      setAvailableSports(sports);
      
      await loadVenueSections();
    };
    loadData();
  }, [venueId]);

  const loadVenueSections = async () => {
    try {
      setLoadingSections(true);
      const { data, error } = await supabase
        .from('venue_sections')
        .select('id, section_name, section_type, field_id')
        .eq('venue_id', venueId)
        .eq('is_active', true)
        .order('section_name');

      if (error) throw error;
      setVenueSections(data || []);
    } catch (error) {
      console.error('Error loading venue sections:', error);
      // Don't show error toast, just continue with manual input
    } finally {
      setLoadingSections(false);
    }
  };

  const scorekeeperNeeded = watch('scorekeeper_needed');

  const onSubmit = async (data: GameFormData) => {
    if (!user?.id) {
      toast.error('Please log in to submit a game');
      return;
    }

    console.log('🎯 GAME SUBMISSION DEBUG - Starting submission');
    console.log('🎯 User data:', {
      id: user.id,
      role: user.role,
      identifier: supabaseUser ? supabaseUser.email : (user as any).username,
      userType: supabaseUser ? 'supabase' : 'internal'
    });
    console.log('🎯 Venue ID:', venueId);
    console.log('🎯 Raw form data:', data);

    setIsSubmitting(true);
    
    try {
      // Prepare the game data with proper formatting
      const gameSubmissionData = {
        venue_id: venueId,
        posted_by: user.id,
        posted_by_role: user.role || 'venue',
        game_title: data.game_title,
        game_description: data.game_description || '',
        game_date: data.game_date,
        game_time: data.game_time,
        field_location: data.field_location || '',
        sport_id: data.sport_id,
        // Handle team IDs - convert to number or null, never 'tbd'
        home_team_id: data.home_team_id && data.home_team_id !== 'tbd' ? Number(data.home_team_id) : null,
        away_team_id: data.away_team_id && data.away_team_id !== 'tbd' ? Number(data.away_team_id) : null,
        scorekeeper_needed: data.scorekeeper_needed,
        scorekeeper_points: Number(data.scorekeeper_points), // Ensure it's a number
        player_participants: data.player_participants || [] // Ensure it's an array
      };

      console.log('🎯 Formatted submission data:', gameSubmissionData);

      const gameData = await venueGamesService.submitGame(gameSubmissionData);

      console.log('🎯 SUCCESS - Game submitted:', gameData);
      
      setGameCode(gameData.game_code);
      toast.success(
        `Game submitted successfully! Game code ${gameData.game_code} has been generated and shared with all relevant parties.`,
        { duration: 8000 }
      );
      reset();
      setHomeTeamName('');
      setAwayTeamName('');
      onSuccess?.();
      
    } catch (error: any) {
      console.error('🎯 ERROR - Game submission failed:', error);
      console.error('🎯 ERROR details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      
      let errorMessage = 'Failed to submit game';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.details) {
        errorMessage = error.details;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyGameCode = () => {
    if (gameCode) {
      navigator.clipboard.writeText(gameCode);
      toast.success('Game code copied to clipboard!');
    }
  };

  if (!user) {
    return (
      <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800">
        <CardContent className="p-6">
          <div className="text-center text-gray-400">
            Please log in to submit a game
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <PermissionGate 
      permission="manage_games"
      fallback={
        <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-white flex items-center gap-2">
              <Trophy className="w-6 h-6 text-primary" />
              Submit Game Request
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <Alert className="border-orange-500/50 bg-orange-500/10">
              <Info className="h-4 w-4 text-orange-400" />
              <AlertDescription className="text-orange-300">
                <strong>Game Creation Restricted:</strong> Only Team Owners, Coaches, and Venues can create games. 
                This ensures proper oversight and safety protocols. If you need a game created, please contact 
                an authorized user in your area.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      }
    >
      <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-white flex items-center gap-2">
            <Trophy className="w-6 h-6 text-primary" />
            Submit Game Request
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <Alert className="mb-6 border-blue-500/50 bg-blue-500/10">
            <Info className="h-4 w-4 text-blue-400" />
            <AlertDescription className="text-blue-300">
              <strong>Who can create games:</strong> Team Owners, Coaches, and Venues only. 
              This ensures proper safety oversight and game management.
            </AlertDescription>
          </Alert>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {gameCode && (
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
              <div className="text-center">
                <h3 className="text-green-400 font-semibold mb-2">Game Successfully Submitted!</h3>
                <p className="text-green-300 text-sm">
                  Game code has been sent to venue, teams, and coaches
                </p>
                <p className="text-green-200 text-xs mt-1">
                  <strong>Check your dashboard for the Game Code</strong>
                </p>
              </div>
            </div>
          )}

          <div className="space-y-2 mb-6">
            <Label className="text-white">Sport Type *</Label>
            <Select onValueChange={(value) => setValue('sport_id', parseInt(value))}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                <SelectValue placeholder="Select sport type" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700 text-white z-50">
                {availableSports.map((sport) => (
                  <SelectItem 
                    key={sport.id} 
                    value={sport.id.toString()}
                    className="text-white hover:bg-gray-700"
                  >
                    {sport.sport_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-400">
              This helps scorekeepers understand what type of game they'll be scoring
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-white">Home Team</Label>
              <TeamSearchCombobox
                value={watch('home_team_id')}
                onSelect={(teamId, teamName) => {
                  setValue('home_team_id', teamId);
                  setHomeTeamName(teamName);
                }}
                placeholder="Select home team"
              />
              {homeTeamName ? (
                <p className="text-sm text-green-400">
                  ✓ Selected: {homeTeamName}
                </p>
              ) : (
                <p className="text-sm text-gray-400">
                  No team selected? Teams can use the game code to register later.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-white">Away Team</Label>
              <TeamSearchCombobox
                value={watch('away_team_id')}
                onSelect={(teamId, teamName) => {
                  setValue('away_team_id', teamId);
                  setAwayTeamName(teamName);
                }}
                placeholder="Select away team"
              />
              {awayTeamName ? (
                <p className="text-sm text-green-400">
                  ✓ Selected: {awayTeamName}
                </p>
              ) : (
                <p className="text-sm text-gray-400">
                  No team selected? Teams can use the game code to register later.
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="game_title" className="text-white">Game Title *</Label>
              <Input
                id="game_title"
                {...register('game_title', { required: 'Game title is required' })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="e.g., Championship Finals"
                value={`${homeTeamName || 'Home Team'} vs ${awayTeamName || 'Away Team'}`}
                readOnly
              />
              {errors.game_title && (
                <p className="text-red-400 text-sm">{errors.game_title.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="field_location" className="text-white">Field/Location</Label>
              {!loadingSections && venueSections.length > 0 ? (
                <Select onValueChange={(value) => setValue('field_location', value)}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Select venue section" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white z-50">
                    {venueSections.map((section) => (
                      <SelectItem 
                        key={section.id} 
                        value={section.section_name}
                        className="text-white hover:bg-gray-700"
                      >
                        <div className="flex items-center justify-between w-full">
                          <span>{section.section_name}</span>
                          <span className="text-xs text-gray-400 ml-2">({section.section_type})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary w-4 h-4" />
                  <Input
                    id="field_location"
                    {...register('field_location')}
                    className="bg-gray-800 border-gray-700 text-white pl-10"
                    placeholder="Field A, Main Court, etc."
                  />
                </div>
              )}
              {!loadingSections && venueSections.length > 0 && (
                <p className="text-sm text-gray-400">
                  Select from available venue sections for better organization
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="game_date" className="text-white">Game Date *</Label>
              <Input
                id="game_date"
                type="date"
                {...register('game_date', { required: 'Game date is required' })}
                className="bg-gray-800 border-gray-700 text-white"
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.game_date && (
                <p className="text-red-400 text-sm">{errors.game_date.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="game_time" className="text-white">Game Time *</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary w-4 h-4" />
                <Input
                  id="game_time"
                  type="time"
                  {...register('game_time', { required: 'Game time is required' })}
                  className="bg-gray-800 border-gray-700 text-white pl-10"
                />
              </div>
              {errors.game_time && (
                <p className="text-red-400 text-sm">{errors.game_time.message}</p>
              )}
              <p className="text-sm text-gray-400">
                Game start time - used for countdown timer when approved
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="game_description" className="text-white">Game Description</Label>
            <Textarea
              id="game_description"
              {...register('game_description')}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="Additional details about the game..."
              rows={3}
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-white">Scorekeeper Needed</Label>
                <p className="text-sm text-gray-400">
                  Do you need a scorekeeper for this game?
                </p>
              </div>
              <Switch
                checked={scorekeeperNeeded}
                onCheckedChange={(checked) => setValue('scorekeeper_needed', checked)}
              />
            </div>

            {scorekeeperNeeded && (
              <div className="space-y-2">
                <Label htmlFor="scorekeeper_points" className="text-white">
                  Scorekeeper Points Reward
                </Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="scorekeeper_points"
                    type="number"
                    {...register('scorekeeper_points', { 
                      valueAsNumber: true,
                      min: { value: 1, message: 'Points must be at least 1' }
                    })}
                    className="bg-gray-800 border-gray-700 text-white pl-10"
                    placeholder="50"
                    min="1"
                  />
                </div>
                <p className="text-sm text-gray-400">
                  Standard scorekeeper points: 50-100 per game
                </p>
                {errors.scorekeeper_points && (
                  <p className="text-red-400 text-sm">{errors.scorekeeper_points.message}</p>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-sports-green text-black hover:bg-sports-green/90 font-bold"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Game Request'}
            </Button>
          </div>
          </form>
        </CardContent>
      </Card>
    </PermissionGate>
  );
};
